import { PaginatedRequest, PaginatedResponse } from '@/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { Company } from '../models/Company';
import { API_FLEET_URL } from '@/utils';

const companiesService = createApi({
  reducerPath: 'companiesService',
  baseQuery: createBaseQueryWithReauth(API_FLEET_URL),
  tagTypes: ['Company', 'Companies'] as const,
  endpoints: (builder) => ({
    getCompany: builder.query<Company, string>({
      query: (companyId) => ({
        url: `/company/${companyId}`,
      }),
      providesTags: ['Company'],
    }),
    getCompanies: builder.query<
      PaginatedResponse<Company>,
      { search?: string; paginated: PaginatedRequest }
    >({
      query: ({ search, paginated }) => ({
        url: '/companies',
        params: {
          name: search || '',
          offset: (paginated.page - 1) * paginated.pageSize,
          limit: paginated.pageSize,
        },
      }),
      providesTags: ['Companies'],
    }),
    createCompany: builder.mutation<Company, Company>({
      query: (body) => ({
        url: '/company',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Companies'],
    }),
    updateCompany: builder.mutation<
      Company,
      { id: string; data: Partial<Company> }
    >({
      query: ({ id, data }) => ({
        url: `/company/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Company', 'Companies'],
    }),
    deleteCompany: builder.mutation<void, string>({
      query: (id) => ({
        url: `/company/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Company', 'Companies'],
    }),
    toggleCompany: builder.mutation<void, string>({
      query: (id) => ({
        url: `/company/${id}/toggle`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Company', 'Companies'],
    }),
  }),
});

export default companiesService;

export const {
  useGetCompanyQuery,
  useGetCompaniesQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useToggleCompanyMutation,
} = companiesService;
